import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../page.css";
import "../../home.css";
import "../../trekking/kilimanjaro.css";
import "../northern-circuits/serengeti.css";
import "./ruaha.css";

const features = [
  {
    title: "Tanzania’s largest park",
    text: "Ruaha covers a vast stretch of southern wilderness. Distances are real, horizons are wide, and you feel the scale of the land on every drive.",
  },
  {
    title: "Great Ruaha River",
    text: "The river and its sand rivers shape the park. In the dry months wildlife gathers along the water, and game viewing becomes focused and intense.",
  },
  {
    title: "Predator country",
    text: "Lions, leopards, cheetahs, and African wild dogs hunt here. Elephant numbers are strong, and antelope like kudu and sable add a southern character.",
  },
  {
    title: "Quieter than the north",
    text: "Fewer vehicles than Serengeti or Ngorongoro on most weeks. Ruaha suits guests who want space, silence between sightings, and a wilder daily rhythm.",
  },
];

const parkStats = [
  { value: "20,226", label: "km² of park" },
  { value: "10,000+", label: "elephants in the ecosystem" },
  { value: "570+", label: "bird species" },
  { value: "Remote", label: "southern wilderness" },
];

const experiences = [
  {
    id: "ruaha-river",
    name: "Great Ruaha River Drives",
    days: "2 to 4 nights",
    style: "Classic dry-season game drives",
    lodging: "Lodge or tented camp",
    bestFor: "Elephants, predators, and river-edge concentrations",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Wildlife along open African river country",
    summary:
      "Follow the Great Ruaha and seasonal sand rivers where elephants, buffalo, and predators gather when water is scarce across the wider park.",
    body: "River drives are the heart of a dry-season Ruaha stay. Guides work the banks, oxbows, and sand riverbeds in morning and late afternoon light. Elephants dig for water, hippos hold deeper pools, and lions often hunt close to the same lines. The park feels enormous, yet the river gives your days a clear spine so you spend more time watching and less time searching empty bush.",
    highlights: [
      "Wildlife concentrated along the Great Ruaha in dry months",
      "Strong elephant herds and mixed predator sightings",
      "Sand river landscapes that photograph beautifully",
      "A focused daily rhythm in a very large park",
    ],
    itinerary: [
      { day: "Morning", text: "Early drive along the river and known water points." },
      { day: "Midday", text: "Return to camp for rest during the heat." },
      { day: "Afternoon", text: "Second drive toward sand rivers, shade trees, and sunset light." },
      { day: "Optional", text: "Guided walk near camp where concessions allow." },
    ],
    notes:
      "Three nights give you room to learn the river system. Two nights still work well if you fly in and keep days full.",
  },
  {
    id: "predators",
    name: "Lions, Leopards, and Wild Dogs",
    days: "Full days within a multi-night stay",
    style: "Predator-focused drives",
    lodging: "Bush lodge or tented camp",
    bestFor: "Guests who want hunting country with fewer vehicles",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open bush and grassland under wide sky",
    summary:
      "Ruaha holds a serious predator population. Lions are often seen in prides, leopards use rocky and riverine cover, and wild dogs may appear when packs are active in the area.",
    body: "Southern Tanzania’s reputation for wild dogs and strong lion country is part of why travelers leave the northern circuit. Sightings are never guaranteed, but the habitat supports them, and lower vehicle numbers mean a sighting can feel private. We keep mornings early and routes flexible so your guide can follow fresh tracks, radio news, and the movement of herds that draw cats and dogs.",
    highlights: [
      "Pride lion country with open viewing in dry months",
      "Leopard potential in riverine and rocky habitat",
      "African wild dog possibilities in season",
      "Fewer vehicles around many sightings",
    ],
    itinerary: [
      { day: "Dawn", text: "Leave camp while cats are still active." },
      { day: "Late morning", text: "Work river edges, kopjes, and known territories." },
      { day: "Afternoon", text: "Return for a second push as light softens." },
      { day: "Evening", text: "Debrief with your guide and set the next day’s priority." },
    ],
    notes:
      "Wild dogs range widely. We plan time in Ruaha rather than chasing a single species across the whole south.",
  },
  {
    id: "baobab-miombo",
    name: "Baobabs and Miombo Woodland",
    days: "Morning or afternoon drives",
    style: "Scenic landscape safari",
    lodging: "Lodge or tented camp",
    bestFor: "Atmosphere, birds, and southern Tanzania character",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Baobab and open woodland landscape",
    summary:
      "Ruaha sits at a meeting point of East and Southern African habitats. Baobabs rise from dry country, while miombo woodland and open grassland create a look different from Serengeti plains.",
    body: "The landscape is half the reason people remember Ruaha. Pale baobabs, rocky outcrops, dry riverbeds, and woodland birds give the park a southern mood. Giraffe, kudu, and sable move through these habitats, and the light turns gold early and late. We keep time for slower photography stops because the scenery is not only a backdrop for game lists.",
    highlights: [
      "Iconic baobab country and rocky horizons",
      "Miombo woodland with rich birdlife",
      "Kudu, giraffe, and other woodland specialists",
      "A distinct look from northern Tanzania parks",
    ],
    itinerary: [
      { day: "Sunrise", text: "Drive baobab country in soft first light." },
      { day: "Morning", text: "Work woodland loops and open clearings." },
      { day: "Afternoon", text: "Return for golden hour among the trees and rocks." },
      { day: "Birding", text: "Pause for rollers, hornbills, and raptors when the group wants." },
    ],
    notes:
      "Ideal when you want a safari that feels different from the classic northern loop, not only fewer people on the same roads.",
  },
  {
    id: "southern-link",
    name: "Southern Circuit Base",
    days: "3 to 5 nights",
    style: "Fly-in safari hub",
    lodging: "Inside park or nearby camp",
    bestFor: "A remote main safari before Nyerere or Zanzibar",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open highland road toward distant horizons",
    summary:
      "Ruaha is often the centrepiece of a southern safari. Fly in from Dar es Salaam or Arusha, settle into a private camp rhythm, then continue to Nyerere or finish on the coast.",
    body: "Road access is long, so most guests fly. Once you land, days are simple: morning drive, rest, afternoon drive, camp fire, sleep. Three nights is a solid start; four or five lets the park open up. From here you can add Nyerere for rivers and boat days, Mikumi for an easier southern stop, or fly on to Zanzibar when the bush days are done.",
    highlights: [
      "Fly-in access that protects time for wildlife",
      "Strong multi-night base for a southern safari",
      "Clean pairing with Nyerere National Park",
      "Natural finish into a Zanzibar beach week",
    ],
    itinerary: [
      { day: "Arrival", text: "Light flight in, transfer to camp, afternoon drive." },
      { day: "Full days", text: "River and woodland drives shaped around season." },
      { day: "Optional", text: "Walking safari or longer day into quieter sectors." },
      { day: "Next", text: "Fly to Nyerere, Dar, Arusha, or Zanzibar." },
    ],
    notes:
      "Tell us your dates and whether you want only Ruaha or a wider southern loop. Flight timing shapes the night count.",
  },
];

const seasons = [
  {
    title: "June to October",
    text: "Peak dry season. Wildlife concentrates on the Great Ruaha and sand rivers. The classic time for clear viewing and strong game density.",
  },
  {
    title: "November to December",
    text: "Short rains arrive. Greener bush, excellent birding, and still solid game with a softer, quieter feel in many weeks.",
  },
  {
    title: "January to March",
    text: "Longer green season. Animals spread out, landscapes look full, and birdlife stays rich for guests who prefer space over concentration.",
  },
  {
    title: "April to May",
    text: "Heavier rains possible. Some camps quieten or close. Beautiful when accessible, best for flexible travelers.",
  },
];

const practical = [
  {
    title: "How long to stay",
    text: "Three nights is a strong base. Four or five nights lets you cover river country and deeper woodland without rushing.",
  },
  {
    title: "Best months",
    text: "June to October for density along the river. Green months reward birders and guests who want fewer visitors.",
  },
  {
    title: "What we arrange",
    text: "Flights, park fees, lodge or tented camp, private guide and vehicle, and links to Nyerere, Mikumi, or Zanzibar.",
  },
];

function Ruaha() {
  const { hash } = useLocation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    document.title = "Ruaha National Park · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStatsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStatsVisible(true);
        observer.disconnect();
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kili-page ruaha-page">
      <section className="kili-hero serengeti-hero ruaha-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7">
              <p className="eyebrow eyebrow--light mb-3">
                <Link
                  to="/safaris/southern-circuits"
                  className="text-decoration-none"
                >
                  Southern Circuit
                </Link>
              </p>
              <h1 className="mb-3">Ruaha National Park</h1>
              <p className="kili-hero__lede mb-4">
                Tanzania’s largest national park. Baobabs, the Great Ruaha
                River, elephants, and predator country with fewer vehicles than
                the northern parks in most seasons.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#experiences">
                  View experiences
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Ruaha
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={statsRef}
        className={
          statsVisible ? "serengeti-stats is-visible" : "serengeti-stats"
        }
        aria-label="Ruaha park facts"
      >
        <div className="container">
          <ul className="serengeti-stats__list list-unstyled mb-0">
            {parkStats.map((stat, index) => (
              <li
                className="serengeti-stats__item"
                key={stat.label}
                style={{ "--stat-delay": `${index * 120}ms` }}
              >
                <span className="serengeti-stats__value">{stat.value}</span>
                <span className="serengeti-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">The park</p>
              <h2 className="mb-3">Why Ruaha leads the southern circuit</h2>
              <p className="page-lead mb-0">
                It is not a quieter copy of the Serengeti. It is a vast southern
                wilderness with its own river systems, baobab horizons, and a
                predator story that rewards guests who want space.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item) => (
              <div className="col-md-6" key={item.title}>
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experiences" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Experiences</p>
              <h2 className="mb-3">How to spend your Ruaha days</h2>
              <p className="page-lead mb-0">
                River corridors, predator country, and baobab woodland each give
                a different mood. We shape the drives around season and how many
                nights you have.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {experiences.map((item, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={item.id}
                id={item.id}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={item.image} alt={item.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {item.days}
                      <span aria-hidden="true"> · </span>
                      {item.style}
                    </p>
                    <h3 className="h3 mb-3">{item.name}</h3>
                    <p className="kili-route__summary">{item.summary}</p>
                    <p className="kili-route__body-copy mb-0">{item.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{item.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>{item.days}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{item.lodging}</dd>
                    </div>
                    <div>
                      <dt>Style</dt>
                      <dd>{item.style}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the day</h4>
                      <ul className="trek-points mb-0">
                        {item.highlights.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {item.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{item.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {item.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Seasons</p>
              <h2 className="mb-3">When Ruaha is at its best</h2>
              <p className="page-lead mb-0">
                Dry months bring the densest gatherings along the river. Green
                months trade some concentration for birds, soft light, and
                quieter tracks.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {seasons.map((item) => (
              <div className="col-md-6 col-lg-3" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Before you go</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item) => (
              <div className="col-md-4" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your safari</p>
              <h2 className="mb-3">
                Tell us your month and how many nights you want in Ruaha.
              </h2>
              <p className="mb-0 home-close__copy">
                We reply with flight options, camp style, and how it connects to
                Nyerere, Mikumi, or a Zanzibar finish.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Ruaha;
